/**
 * `to` with an incoming query string folded in, **destination-wins**.
 *
 * The leaf beside {@link RedirectPreservingSearch} — a pure function, so it
 * lives in its own module rather than being exported from a `.tsx` that also
 * exports a component (which costs that file fast refresh, and is what
 * `react-refresh/only-export-components` is telling you).
 *
 * ## Why destination-wins, and why it is not concatenation
 *
 * A redirect for a MERGED surface routinely names a tab in `to` while the old
 * path carried a `?tab=` of its own with a different vocabulary —
 * `/page-rules?tab=all` → `/engagement?tab=placement` is exactly that. The
 * obvious `${to}${search}` produces `?tab=placement?tab=all`: not two params,
 * ONE param whose value is `placement?tab=all`, which fails the destination's
 * parse and lands it on its default tab. Silently, on a URL somebody bookmarked.
 *
 * Merging with the destination winning keeps the stale key out and carries
 * everything else — which is what lets `/proactive?nudge=<id>` still arrive with
 * the drawer open after the Engagement merge.
 *
 * A relative `to` and a `to` with a hash both survive, because the URL is
 * rebuilt from its parts rather than string-spliced.
 */
export function mergeRedirectSearch(to: string, search: string): string {
  if (search === '' || search === '?') return to;

  // Split `to` by hand rather than through `new URL()`: these destinations are
  // app-relative (`/engagement?tab=nudges`), and `URL` demands a base, which
  // would mean inventing an origin only to strip it off again.
  const hashAt = to.indexOf('#');
  const hash = hashAt === -1 ? '' : to.slice(hashAt);
  const withoutHash = hashAt === -1 ? to : to.slice(0, hashAt);

  const queryAt = withoutHash.indexOf('?');
  const path = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);

  // Seeded from the DESTINATION, then the incoming params it does not name are
  // appended. Both halves of that are deliberate: seeding from the destination
  // is what makes it win a shared key (nothing below can overwrite what is
  // already here), and it also puts the destination's own params first, so the
  // URL a person ends up looking at reads `?tab=nudges&nudge=pricing` rather
  // than the other way round.
  const merged = new URLSearchParams(queryAt === -1 ? '' : withoutHash.slice(queryAt));
  const incoming = new URLSearchParams(search);
  for (const key of new Set(incoming.keys())) {
    if (merged.has(key)) continue;
    // `append` per value rather than `set`: a key the old URL repeated is
    // carried through as it was. Collapsing it would silently drop a value from
    // a URL somebody saved, and what the destination makes of two values is its
    // own parse's business — it was already facing exactly that before the
    // redirect existed.
    for (const value of incoming.getAll(key)) merged.append(key, value);
  }

  const query = merged.toString();
  return `${path}${query === '' ? '' : `?${query}`}${hash}`;
}
